/* eslint-disable no-empty-function */
/* eslint-disable @typescript-eslint/no-empty-function */
import * as React from 'react'
import styles from './button.component.styl'
import cx from 'classnames'
import Tooltip from '@src/components/common/tooltip'
import CAMIcon from '../icon'
import { getHTMLRoleBy } from '@src/utils/css.selector.helper'
import { Link } from 'react-router-dom'

export type IconProps = (MaterialIconProps | CAMIconProps) & {
  iconType?: 'material-icons' | 'cam-icons'
}

interface MaterialIconProps {
  iconType?: 'material-icons'
  icon?: string
}

interface CAMIconProps {
  iconType?: 'cam-icons'
  icon?: CAMIconName
}

export type ButtonStyle =
  | 'primary'
  | 'secondary'
  | 'caution'
  | 'alert'
  | 'none'
  | 'outlined'
export type Props = IconProps & {
  id?: string
  role?: string
  disabled?: boolean
  title?: React.ReactNode
  iconPosition?: 'left' | 'right'
  style?: ButtonStyle
  className?: string
}
export type ButtonProps = Props & {
  description?: React.ReactNode
  onClick: (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>
  ) => void
  pulsating?: boolean
}

export type ToogleButtonProps = Props & {
  toogle: 'on' | 'off'
  description?: React.ReactNode
  onClick?: (
    state: 'on' | 'off',
    event?: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void
}

export type SubmitButtonProps = Props & {
  description?: React.ReactNode
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}
export type ButtonLinkProps = Props & {
  description?: React.ReactNode
  to: string
  type?: 'internal' | 'external'
}

export type IconButtonProps = Props & {
  description?: React.ReactNode
  onClick: (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>
  ) => void
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  id,
  title,
  onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {},
  className,
  style = 'primary',
  children,
  description,
  disabled
}: React.PropsWithChildren<SubmitButtonProps>) => {
  function handleClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (!disabled && onClick && typeof onClick === 'function') {
      onClick(event)
    }
  }
  return (
    <Tooltip description={description}>
      <button
        onClick={handleClick}
        disabled={disabled}
        id={id}
        type="submit"
        className={cx(styles.button, className, styles[style], {
          [styles.disabled]: disabled
        })}
      >
        <span role="title">{children || title}</span>
      </button>
    </Tooltip>
  )
}

const getRole = (title: React.ReactNode): string =>
  typeof title === 'string'
    ? (getHTMLRoleBy(title, 'button_', 'value') as string)
    : null

const getAttribute = (
  type: 'button' | 'icon',
  {
    id,
    role,
    title,
    className,
    style = 'primary',
    iconPosition,
    disabled
  }: ButtonProps | ButtonLinkProps | IconButtonProps
) => ({
  id,
  role: role || getRole(title),
  className: cx(className, styles.button, styles[style], {
    [styles.disabled]: disabled,
    [styles.reverse]: iconPosition === 'right'
  })
})

type ButtonIconProps = IconProps & {}

export const Icon: React.FC<ButtonIconProps> = ({
  icon,
  iconType
}: ButtonIconProps) => {
  const isMaterialIcon = iconType === 'material-icons'
  return isMaterialIcon ? (
    <i className={cx(styles.icon, 'material-icons')}>{icon}</i>
  ) : (
    <CAMIcon className={cx(styles.camIcon)} name={icon as CAMIconName} />
  )
}

const Button: React.FC<ButtonProps> = (
  props: React.PropsWithChildren<ButtonProps>
) => {
  const { title, onClick, children, disabled, description } = props
  function handleClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!disabled && onClick && typeof onClick === 'function') {
      onClick(event)
    }
  }
  return (
    <Tooltip description={description}>
      <div {...getAttribute('button', props)} onClick={handleClick}>
        <Icon {...props} />
        <span role="title">{children || title}</span>
      </div>
    </Tooltip>
  )
}

export const ToogleButton: React.FC<ToogleButtonProps> = (
  props: React.PropsWithChildren<ToogleButtonProps>
) => {
  const { onClick, disabled, toogle } = props

  function handleClick(
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>
  ) {
    if (!disabled && onClick && typeof onClick === 'function') {
      onClick(
        toogle === 'on' ? 'off' : 'on',
        event as React.MouseEvent<HTMLDivElement, MouseEvent>
      )
    }
  }
  const btnProps: ButtonProps = { ...props, onClick: null }
  return <Button {...btnProps} onClick={handleClick} />
}

export const IconButton: React.FC<IconButtonProps> = (
  props: React.PropsWithChildren<IconButtonProps>
) => {
  const { onClick, children, disabled, description } = props
  function handleClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!disabled && onClick && typeof onClick === 'function') {
      onClick(event)
    }
  }

  return (
    <Tooltip description={description}>
      <div {...getAttribute('icon', props)} onClick={handleClick}>
        <Icon {...props} />
        {children}
      </div>
    </Tooltip>
  )
}

export const LinkButton: React.FC<ButtonLinkProps> = (
  props: React.PropsWithChildren<ButtonLinkProps>
) => {
  const {
    title,
    children,
    to,
    disabled,
    description,
    type = 'internal'
  } = props

  const href = !disabled ? to : null
  const commonProps = {
    ...getAttribute('button', props)
  }

  const bodyComponent = (
    <>
      <Icon {...props} />
      <span role="title">{children || title}</span>
    </>
  )
  return (
    <Tooltip description={description}>
      {type === 'external' ? (
        <a {...commonProps} href={href}>
          {bodyComponent}
        </a>
      ) : (
        <Link {...commonProps} to={href}>
          {bodyComponent}
        </Link>
      )}
    </Tooltip>
  )
}

export default Button
